import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RolesWithAllMunicipalitiesVisible, RolesWithRegionMunicipalitiesAssignment } from '@app/shared/constants';
import { MunicipalityKey, PermissionCode, Roles, Tenant, UserStatus } from '@app/shared/enums';
import { StringUtility } from '@app/shared/string-utility';
import { environment } from '@env/environment';
import { assign, differenceBy, flatMap, isEmpty, sortBy, uniq } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import {
  ActiveKamUser,
  ActiveKamUserDetails,
  ExtendedUserData,
  ExtendedUsersResponse,
  GetServiceBusUserDetails,
  JobFunction,
  MunicipalitiesResponse,
  Municipality,
  MunicipalityRestrictions,
  MunicipalityRestrictionsResponse,
  PermissionDetails,
  Region,
  RegionsResponse,
  RolePermissions,
  ServiceBusPayload,
  ServiceBusUser,
  User,
  UserData,
  UserMunicipalities,
  UserPermissions,
  UserPermissionsResponse,
  UserRegions,
  UserRoleAndMunicipalityOrUserRegionsResponse,
  UsersResponse,
} from '../../models/user';
import { UserRegionService } from './user-regions.service';
import { UserRegistration } from '@app/registration-request/model/user-registration';

@Injectable({
  providedIn: 'root',
})
export class UserService extends ApiService {
  constructor(
    public http: HttpClient,
    private readonly userRegionService: UserRegionService
  ) {
    super(http);
  }

  private activeKamUsers$?: Observable<ActiveKamUser[]>;
  baseUri = 'socrates/authz/api/users';
  municipalitiesBaseUri = 'socrates/authz/api/municipalities';
  regionsBaseUri = 'socrates/authz/api/regions';
  middleTierBaseUri = 'api/users';
  authzViewsUri = 'socrates/authz/api/views';
  dbColumnNames = [
    'Guid',
    'ItemId',
    'RoleId',
    'Title',
    'Firstname',
    'Middlename',
    'Lastname',
    'Displayname',
    'EMail',
    'EMail2',
    'Telefon',
    'Fax',
    'Mobil',
    'Area',
    'Company',
    'Municipality',
    'District',
    'Street',
    'HouseNumber',
    'HouseNumberAdditive',
    'Room',
    'LocationInfo',
    'ZipCode',
    'AssignedMunicipalityId',
    'MalFunctionNotificationStatus',
    'RexToken',
    'IsRexUser',
    'IsKamUser',
    'IsDsgvoAccepted',
    'Status',
    'ImageName',
    'InvitedBy',
    'InvitationDate',
    'RegistrationDate',
    'LastLoginDate',
    'CreationDate',
    'ModifyDate',
    'ModifyUser',
    'AllowToReceiveWorkOrderEmails',
    'DelegateUserId',
    'UserId',
    'IDPName',
    'LegalDisclaimerVersion',
    'LegalDisclaimerAcceptanceDate',
  ];

  public getByEmail(email: string): Observable<User[]> {
    const encodedEmail = encodeURIComponent(email);
    return this.http.get<User[]>(this.endpoint(this.baseUri, `?$filter=email eq '${encodedEmail}'`));
  }

  public isExistingUser(email: string): Observable<boolean> {
    const encodedEmail = encodeURIComponent(email);
    return this.http.get<boolean>(this.directEndpoint('api', 'users', `hasUser?email=${encodedEmail}`));
  }

  public getByUserId(userId: string): Observable<User> {
    const encodeduserId = encodeURIComponent(userId);
    return this.http
      .get<User[]>(this.endpoint(this.baseUri, `?$filter=userId eq '${encodeduserId}'`))
      .pipe(map((users: User[]) => (users?.length > 0 ? users[0] : ({} as User))));
  }

  public getByUserGuid(guid: string): Observable<User> {
    const func: any = (a: any) => (a && a.length > 0 ? a[0] : null);

    return this.http.get<User[]>(this.endpoint(this.baseUri, guid)).pipe(
      map((a) => {
        return func(a);
      })
    );
  }

  public getNameByGuid(guid: string): Observable<Partial<User> | null> {
    return this.http.get<{ firstName: string; lastName: string } | null>(this.endpoint(this.middleTierBaseUri, guid, 'name')).pipe(
      map((user: { firstName: string; lastName: string } | null) =>
        user
          ? {
              firstname: user.firstName,
              lastname: user.lastName,
            }
          : null
      )
    );
  }

  public getEmailByUserGuid(guid: string): Observable<User> {
    return this.http.get<User>(this.endpoint('api/userregistration', `primarykam/${guid}/email`));
  }

  public submitRegistrationRequest(registrationData: UserRegistration, selectedMunicipalityGuid: string, isMunicipalityWorker: boolean): Observable<void> {
    return this.http.post<void>(this.directEndpoint(`/api/userregistration/request`), {
      LastName: registrationData.LastName,
      FirstName: registrationData.FirstName,
      Role: registrationData.Role,
      Email: registrationData.Email,
      Telephone: registrationData.Telephone,
      Comment: registrationData.Comment,
      selectedLocationId: selectedMunicipalityGuid,
      roleCategory: isMunicipalityWorker ? 'municipalityWorker' : 'administrationAreaWorker',
    });
  }

  public post(user: User): Observable<User> {
    return this.http.post<User>(this.endpoint(this.baseUri), user);
  }

  public patch(id: string, user: Partial<User>): Observable<User> {
    const payload = this.patchMapColumnNames(user, this.dbColumnNames);
    return this.http.patch<User>(this.endpoint(this.baseUri, id), payload);
  }

  public getAll(query: string): Observable<User[]> {
    return this.http.get<User[]>(this.endpoint(`${this.baseUri}${query}`));
  }

  public getUserServiceBusDetails(userGuid: string): Observable<GetServiceBusUserDetails> {
    let url = '';
    url += `?$filter=guid eq ${userGuid}`;
    url += '&$expand=UserRegions($select=RegionId),';
    url += 'Role($select=Name,Code),';
    url += 'AssignedMunicipality($select=Name,municipalityKey)';
    return this.http.get<GetServiceBusUserDetails[]>(this.endpoint(`${this.baseUri}${url}`)).pipe(
      switchMap((userData: GetServiceBusUserDetails[]) => {
        if (userData[0].role.code === 'AAW') {
          return this.userRegionService.getMunicipalitiesByRegionId(userData[0].userRegions[0].regionId).pipe(
            map((municipalities) => municipalities.map((item) => item.municipality)),
            switchMap((filteredMunicipalities) => {
              userData[0].municipalities = filteredMunicipalities ?? null;
              return userData;
            })
          );
        } else {
          return userData;
        }
      })
    );
  }

  public getUsers(query?: string): Observable<ExtendedUsersResponse> {
    const url = `${this.baseUri}${query}`;

    return this.http.get<UsersResponse>(this.endpoint(url)).pipe(
      map((usersResponse: UsersResponse): ExtendedUsersResponse => {
        return assign({
          data: usersResponse.data.map((info: UserData) =>
            assign({
              ...info,
              delegateFirstName: info?.delegateUser?.firstname,
              delegateLastName: info?.delegateUser?.lastname,
              delegateEmail: info?.delegateUser?.email,
              rolesName: info?.role?.name,
              rolesCode: info?.role?.code,
              roleLevel: info?.role?.roleLevel,
              managedRoles: info?.role?.managedRoles,
              municipalityKey: info?.assignedMunicipality?.municipalityKey,
              municipalityName: info?.assignedMunicipality?.name,
              isPriviledgedAccess: info?.isPriviledgedAccess,
              regions: info?.userRegions?.map((data: UserRegions) => data.region.name).join(),
            } as ExtendedUserData)
          ),
          maxRowCount: usersResponse.maxRowCount,
        } as ExtendedUsersResponse);
      })
    );
  }

  public uploadImage(id: string, path: string, rawFile: File, fileName: string) {
    const formData = new FormData();
    formData.append('Path', path);
    formData.append('File', rawFile, fileName);
    return this.http.post(this.directEndpoint(this.middleTierBaseUri, `${id}/image`), formData);
  }

  public getRegistrationDetails(guid: string): Observable<any> {
    return this.http.get<any>(this.directEndpoint(this.middleTierBaseUri, `${guid}/registration`));
  }

  public registerUser(guid: string, userRegistration: any): Observable<void> {
    return this.http.post<void>(this.directEndpoint(this.middleTierBaseUri, `${guid}/registration`), userRegistration);
  }

  public registerGemisUser(userRegistration: any): Observable<User> {
    return this.http.post<User>(this.directEndpoint(this.middleTierBaseUri, `request-registration`), userRegistration);
  }

  public registerUserRegion(user: any): Observable<void> {
    return this.http.post<void>(this.directEndpoint(`api/userregistration/${user.guid}/regions/${user.regionId}`), user);
  }

  public getActiveKamUsers(): Observable<ActiveKamUser[]> {
    const filterQuery = `$filter=firstname ne null and lastname ne null and guid ne null and status eq '${UserStatus.active}' and role/code eq '${Roles.keyAccountManager}'`;
    const selectQuery = `$select=email,firstname,guid,lastname`;
    const uri = `?${filterQuery}&${selectQuery}`;
    if (!this.activeKamUsers$) {
      this.activeKamUsers$ = this.http.get<ActiveKamUserDetails[]>(this.endpoint(this.baseUri, uri)).pipe(
        map((activeKamUsers: ActiveKamUserDetails[]): ActiveKamUser[] =>
          activeKamUsers.map((user: ActiveKamUserDetails) => ({
            displayName: `${user.firstname} ${user.lastname} (${user.email})`,
            name: `${user.firstname} ${user.lastname}`,
            ...user,
          }))
        ),
        map((kamUsers: ActiveKamUser[]): ActiveKamUser[] => sortBy(kamUsers, ['name'])),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.activeKamUsers$;
  }

  public deleteCIAMUser(egcid: string) {
    return this.http.delete(this.endpoint(this.middleTierBaseUri, `egcid/${egcid}`));
  }

  public deactivateUser(userId: string, deletedBy: string, deletedByRole: string) {
    const DeactivationHeaders = {
      DeletedBy: 'Deleted-By',
      DeletedByRole: 'Deleted-By-Role',
    };

    const headers = new HttpHeaders({
      [DeactivationHeaders.DeletedBy]: deletedBy,
      [DeactivationHeaders.DeletedByRole]: deletedByRole,
    });

    return this.http.delete(this.endpoint(this.middleTierBaseUri, `guid/${userId}/deactivation`), { headers });
  }

  public updateCIAMUser(egcId: string, user: any) {
    return this.http.put(this.endpoint(this.middleTierBaseUri, `egcid/${egcId}`), user);
  }

  public postMessageToServiceBus(egcId: string, user: ServiceBusPayload) {
    return this.http.post(this.endpoint(this.middleTierBaseUri, `${egcId}/account-management-sb`), user);
  }

  public getUserMunicipalities(userGuid: string): Observable<UserMunicipalities[]> {
    return this.http
      .get<
        UserRoleAndMunicipalityOrUserRegionsResponse[]
      >(this.endpoint(`${this.baseUri}?$filter=guid eq ${userGuid} OR delegateUserId eq ${userGuid}&$expand=assignedMunicipality($select=isActive,isDeactivated,guid,municipalityKey,name,zipCode),userRegions($select=regionId),role($select=code)&$select=assignedMunicipality,userRegions,Role`))
      .pipe(
        switchMap((user: UserRoleAndMunicipalityOrUserRegionsResponse[]) => this.determineUserMunicipalities(user)),
        catchError(() => of([]))
      );
  }

  private determineUserMunicipalities(users: UserRoleAndMunicipalityOrUserRegionsResponse[]): Observable<UserMunicipalities[]> {
    const user = users[0];
    const roleCode = user.role.code.trim().toUpperCase();
    const getAllMunicipalities$: Observable<MunicipalitiesResponse[]> = this.http.get<MunicipalitiesResponse[]>(
      this.endpoint(`${this.municipalitiesBaseUri}?$select=isActive,isDeactivated,guid,municipalityKey,name,zipCode`)
    );

    const getRegionMunicipalities$ = (regionIdCollection: string): Observable<RegionsResponse[]> => {
      return this.http.get<RegionsResponse[]>(
        this.endpoint(
          `${this.regionsBaseUri}?$filter=guid in (${regionIdCollection})&$expand=regionMunicipalities($select=municipality;$expand=municipality($select=guid,municipalityKey,name,zipCode,isActive,isDeactivated))&$select=regionMunicipalities`
        )
      );
    };

    if (StringUtility.areStringsIdentical(roleCode, Roles.municipalityWorker)) {
      return of(user).pipe(
        map((userResp: UserRoleAndMunicipalityOrUserRegionsResponse): UserMunicipalities[] => [
          {
            isActive: userResp.assignedMunicipality.isActive,
            isDeactivated: userResp.assignedMunicipality.isDeactivated,
            municipalityId: userResp.assignedMunicipality.guid,
            municipalityKey: userResp.assignedMunicipality.municipalityKey,
            municipalityName: userResp.assignedMunicipality.name,
            zipCode: userResp.assignedMunicipality.zipCode,
          },
        ])
      );
    } else if (StringUtility.hasAnyMatchInArray(RolesWithAllMunicipalitiesVisible, [roleCode])) {
      return getAllMunicipalities$.pipe(
        map((allMunicipalities: MunicipalitiesResponse[]): UserMunicipalities[] =>
          allMunicipalities.map((municipality: MunicipalitiesResponse) => ({
            isActive: municipality.isActive,
            isDeactivated: municipality.isDeactivated,
            municipalityId: municipality.guid,
            municipalityKey: municipality.municipalityKey,
            municipalityName: municipality.name,
            zipCode: municipality.zipCode,
          }))
        )
      );
    } else if (StringUtility.hasAnyMatchInArray(RolesWithRegionMunicipalitiesAssignment, [roleCode])) {
      const regionIdCollection = uniq(flatMap(users, 'userRegions').map((region: Region) => `'${region.regionId}'`)).join();
      return getRegionMunicipalities$(regionIdCollection).pipe(
        map((regions: RegionsResponse[]): UserMunicipalities[] =>
          flatMap(
            regions.map((region: RegionsResponse) =>
              region.regionMunicipalities.map((regionMunicipality: Municipality) => ({
                isActive: regionMunicipality.municipality.isActive,
                isDeactivated: regionMunicipality.municipality.isDeactivated,
                municipalityId: regionMunicipality.municipality.guid,
                municipalityKey: regionMunicipality.municipality.municipalityKey,
                municipalityName: regionMunicipality.municipality.name,
                zipCode: regionMunicipality.municipality.zipCode,
              }))
            )
          )
        )
      );
    } else {
      return of([]);
    }
  }

  public getUserPermissions(userGuid: string, municipalityId: string): Observable<UserPermissions[]> {
    const getCurrentMunicipalityRestrictions$: Observable<MunicipalityRestrictionsResponse[]> = this.http.get<MunicipalityRestrictionsResponse[]>(
      this.endpoint(
        `${this.municipalitiesBaseUri}?$filter=guid eq ${municipalityId} and MunicipalityRestrictions/any(m:m/Permission/featureFunction eq 'PAGE')&$expand=MunicipalityRestrictions($filter=Permission/featureFunction eq 'PAGE' and isDeleted eq false;$select=permissionId,isTeaser;$expand=Permission($select=code))&$select=MunicipalityKey,MunicipalityRestrictions`
      )
    );

    const getUserPermissions$: Observable<UserPermissionsResponse[]> = this.http.get<UserPermissionsResponse[]>(
      this.endpoint(
        `${this.baseUri}?$filter=guid eq ${userGuid}&$expand=Role($select=guid,name,code;$expand=RolePermissions($select=Permission;$filter=Permission/isActive eq true;$expand=Permission($select=guid,code)))&$select=Role`
      )
    );

    let restrictedMunicipalityPermissions: PermissionDetails[];
    let municipalityKey = '';

    const municipalityRestrictions$: Observable<PermissionDetails[]> = getCurrentMunicipalityRestrictions$.pipe(
      tap((resp: MunicipalityRestrictionsResponse[]) => (municipalityKey = resp[0].municipalityKey)),
      map((currentMunicipalityRestrictionsResponse: MunicipalityRestrictionsResponse[]): PermissionDetails[] =>
        flatMap(
          currentMunicipalityRestrictionsResponse.map((municipalityRestrictionsResponse: MunicipalityRestrictionsResponse) =>
            municipalityRestrictionsResponse.municipalityRestrictions.map((municipalityRestrictions: MunicipalityRestrictions) => ({
              permissionCode: municipalityRestrictions?.permission?.code,
              permissionId: municipalityRestrictions?.permissionId,
              isTeaser: municipalityRestrictions?.isTeaser,
            }))
          )
        )
      ),
      catchError(() => of([]))
    );

    return municipalityRestrictions$.pipe(
      concatMap((restrictedPermissions: PermissionDetails[]) => {
        restrictedMunicipalityPermissions = restrictedPermissions;
        return getUserPermissions$;
      }),
      map((userPermissionsResponse: UserPermissionsResponse[]): UserPermissions[] =>
        flatMap(
          userPermissionsResponse.map((userPermissions: UserPermissionsResponse) =>
            userPermissions.role.rolePermissions.map((rolePermissions: RolePermissions) => ({
              isTeaser:
                restrictedMunicipalityPermissions.find((permissions: PermissionDetails) =>
                  StringUtility.areStringsIdentical(permissions.permissionId, rolePermissions.permission.guid)
                )?.isTeaser ?? false,
              permissionCode: rolePermissions?.permission?.code,
              permissionId: rolePermissions?.permission?.guid,
              roleCode: userPermissions?.role?.code,
              roleName: userPermissions?.role?.name,
              roleid: userPermissions?.role?.guid,
            }))
          )
        )
      ),
      map((userPermissions: UserPermissions[]): UserPermissions[] => {
        restrictedMunicipalityPermissions = restrictedMunicipalityPermissions.filter(
          (restrictedPermissions: PermissionDetails) => !restrictedPermissions.isTeaser
        );
        return differenceBy(userPermissions, restrictedMunicipalityPermissions, 'permissionCode');
      }),
      map((userPermissions: UserPermissions[]): UserPermissions[] => {
        const isTenantBag = StringUtility.areStringsIdentical(environment.tenant, Tenant.BAYERNWERK);
        const isMunicipalitySchwandorf = StringUtility.areStringsIdentical(municipalityKey, MunicipalityKey.SCHWANDORF);
        if (isTenantBag && !isMunicipalitySchwandorf) {
          userPermissions = userPermissions.filter(
            (permission: UserPermissions) => !StringUtility.areStringsIdentical(permission.permissionCode, PermissionCode.INNO_LIVE)
          );
        }
        return userPermissions;
      }),
      catchError(() => of([]))
    );
  }

  public getDelegates(guid: string): Observable<any[]> {
    return this.http.get<any[]>(this.endpoint(this.baseUri, `?$filter=delegateUserId eq ${guid}`));
  }

  public getUserByGuid(guid: string): Observable<any[]> {
    return this.http.get<any[]>(this.endpoint(`socrates/authz/api/users/${guid}?$expand=role`));
  }

  public getFullNameByGuid(guid: string[]): Observable<any[]> {
    return this.http.get<any[]>(this.endpoint(`socrates/authz/api/users?$filter=guid in (\'${guid.join("','")}\')&$select=firstname,lastname,guid`));
  }

  public getUserPermissionByRoleGuid(guid: string): Observable<any[]> {
    return this.http
      .get<any[]>(this.endpoint(`socrates/authz/api/roles/${guid}/permissions?$select=permission&$expand=permission`))
      .pipe(map((x) => x.map((p) => p.permission)));
  }

  public getLegalDisclaimerVersionByGuid(guid: string): Observable<any[]> {
    return this.http.get<any[]>(this.endpoint(`socrates/authz/api/users/${guid}?$select=legalDisclaimerVersion`));
  }

  public getJobFunctions(): Observable<JobFunction[]> {
    return this.http.get<JobFunction[]>(this.directEndpoint('api', 'users', 'jobfunctions'));
  }

  public getRoles(): Observable<any> {
    return this.http.get(this.directEndpoint('api/userregistration/roles'));
  }

  public createServiceBusPayload(user: ServiceBusUser): Observable<ServiceBusPayload> {
    let userDetails: ServiceBusPayload = {
      ...user,
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      phoneNumber: user.telefon,
      idpname: user.idpname,
      roleCode: user.rolesCode,
      municipalities: user.municipalities ?? [],
      action: user.action,
    };

    if (isEmpty(user.municipalities)) {
      switch (user.rolesCode) {
        case Roles.administrationAreaWorker:
          return this.getUserMunicipalities(user.guid).pipe(
            map((municipalities: UserMunicipalities[]) => ({
              ...userDetails,
              municipalities: municipalities.map(({ municipalityName, municipalityKey }) => ({
                name: municipalityName,
                municipalityKey: municipalityKey,
              })),
            }))
          );

        case Roles.municipalityWorker:
          return of({
            ...userDetails,
            municipalities: user.assignedMunicipality ? [user.assignedMunicipality] : [],
          });

        default:
          return of(userDetails);
      }
    }

    return of(userDetails);
  }
}
